import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Logo } from './Logo';
import { Icon, type IconName } from './Icon';
import { ShellMapHost } from './MapView';
import { themeVars, useTheme } from '../theme/theme';
import { useApp } from '../lib/app';
import { initials } from '../lib/format';
import { LANGS, useLang, type Lang } from '../lib/i18n';
import { useLayout } from '../lib/layout';

const RIDE_PATHS = ['/home', '/search', '/choose', '/pickup', '/ride'];
const SHORT: Record<Lang, string> = { en: 'EN', hinglish: 'Hinglish', hi: 'हिंदी' };

/** Sign-in screens draw their own split hero (no shared map, no top bar). */
export const isAuthPath = (path: string) => path === '/' || path.startsWith('/signin') || path === '/__art';

/**
 * The wide-screen frame (tablet and desktop): a top bar with the brand, Ride · Trips · Account, the language and
 * the rider; under it one shared live map, with the screen's panel on its left. Phones never render this.
 */
export function Shell({ path, children }: { path: string; children: ReactNode }) {
  const L = useLayout();
  const { mode } = useTheme();
  const { draft } = useApp();
  const auth = isAuthPath(path);
  const pub = path.startsWith('/t/');
  const bar = !L.compact && !auth;
  return (
    <div className="zr-shell zr-hosted" data-theme={mode} style={themeVars(mode, 'map')}>
      {bar && <TopNav path={path} pub={pub} />}
      <div className="zr-stage" style={{ top: bar ? 'var(--topbar)' : 0 }}>
        {auth ? (
          children
        ) : (
          <ShellMapHost center={draft.pickup}>
            {children}
          </ShellMapHost>
        )}
      </div>
    </div>
  );
}

/**
 * Phones: the same single map for the whole signed-in session, under every screen. Page screens (Home, Search,
 * Trips, Account) cover it; the booking and ride steps are see-through over it with their sheet, so Choose ride →
 * Confirm pickup → the live ride never re-create the map — the camera just eases to each framing.
 */
export function PhoneShell({ path, children }: { path: string; children: ReactNode }) {
  const { mode } = useTheme();
  const { draft, signedIn } = useApp();
  if (isAuthPath(path) || path.startsWith('/t/') || !signedIn) return <>{children}</>;
  return (
    <div className="zr-phone zr-hosted" data-theme={mode} style={themeVars(mode, 'map')}>
      <ShellMapHost center={draft.pickup}>{children}</ShellMapHost>
    </div>
  );
}

function TopNav({ path, pub }: { path: string; pub: boolean }) {
  const { mode } = useTheme();
  const { rider } = useApp();
  const { t, lang, setLang } = useLang();
  const active = RIDE_PATHS.some((p) => path.startsWith(p)) ? 'ride' : path.startsWith('/trips') ? 'trips' : path.startsWith('/account') ? 'account' : null;
  const tab = (key: 'ride' | 'trips' | 'account', to: string, label: string, icon: IconName) => (
    <Link to={to} className="zr-topnav-link" aria-current={active === key ? 'page' : undefined}>
      <Icon name={icon} size={20} sw={active === key ? 2.1 : 1.9} />
      <span>{label}</span>
    </Link>
  );
  return (
    <header className="zr-topbar" style={themeVars(mode, 'base')}>
      <Link to={pub ? '/' : '/home'} className="zr-brand" aria-label="Zoomo Rides">
        <Logo size={36} />
        <span>Zoomo Rides</span>
      </Link>
      {!pub && (
        <nav aria-label={t('nav.main')} className="zr-topnav">
          {tab('ride', '/home', t('nav.ride'), 'ride')}
          {tab('trips', '/trips', t('nav.trips'), 'history')}
          {tab('account', '/account', t('nav.account'), 'user')}
        </nav>
      )}
      <span style={{ flex: 1 }} />
      <div role="radiogroup" aria-label={t('lang.title')} className="zr-langs">
        {LANGS.map((l) => (
          <button key={l} type="button" role="radio" aria-checked={l === lang} aria-label={t(`lang.${l}`)} onClick={() => setLang(l)}>
            {SHORT[l]}
          </button>
        ))}
      </div>
      {!pub && (
        <Link to="/account" className="zr-topbar-me" aria-label={t('home.account')}>
          {initials(rider?.name) || <Icon name="user" size={20} />}
        </Link>
      )}
    </header>
  );
}
